import { Icon } from '@iconify-icon/react';

import { DEMO_BRAND as C, useScrollReveal } from './_hooks';

/* ── 25-minute agenda timeline ─────────────────────────
   Vertical timeline with stagger reveal. Each step
   shows what the audience does + what we do. ─────── */

const steps = [
  {
    time: '0:00',
    duration: '5 min',
    title: 'Coffee + context',
    youDo: 'Tell us about the workflow you wish were automatic.',
    weDo: 'No slide deck — we ask questions, you answer.',
    color: C.blue,
  },
  {
    time: '5:00',
    duration: '7 min',
    title: 'Your docs go in',
    youDo: 'Drop three real files you brought to the call.',
    weDo: 'You watch ingestion, chunking, and embedding in the builder.',
    color: C.indigo,
  },
  {
    time: '12:00',
    duration: '6 min',
    title: 'Build your agent',
    youDo: 'Pick a tone, pick a model, write one instruction.',
    weDo: 'Live, on screen, in the chat.co builder UI.',
    color: C.amber,
  },
  {
    time: '18:00',
    duration: '5 min',
    title: 'Ask it anything',
    youDo: 'Throw your hardest questions at it. The trickier the better.',
    weDo: "We show citations, swap models, prove it isn't hallucinating.",
    color: C.green,
  },
  {
    time: '23:00',
    duration: '2 min',
    title: 'You keep the agent',
    youDo: 'Decide where it should live — widget, API, internal portal.',
    weDo: 'Hand off credentials. Schedule a 7-day check-in.',
    color: C.blue,
  },
];

const DemoAgenda: React.FC = () => {
  const [headerRef, headerVisible] = useScrollReveal(0.4);

  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-32">
      {/* Side rule */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 bg-[#0a1024]/[0.05] sm:block" />

      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <header
          ref={headerRef}
          className="text-center transition-all duration-700"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
          }}>
          <div className="inline-flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#0a1024]/40" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0a1024]/60">
              Minute-by-minute
            </span>
            <span className="inline-block h-px w-8 bg-[#0a1024]/40" />
          </div>
          <h2
            className="mt-6 text-[36px] leading-[1.05] tracking-tight text-[#0a1024] sm:text-[48px]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 400 }}>
            What 25 minutes{' '}
            <span
              style={{
                fontStyle: 'italic',
                fontWeight: 500,
                fontVariationSettings: '"SOFT" 100, "opsz" 144',
                color: '#004790',
              }}>
              actually
            </span>{' '}
            looks like.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-[16px] leading-relaxed text-slate-500">
            We respect your calendar. The clock starts when you say hello and it stops on the dot.
          </p>
        </header>

        <ol className="mt-16">
          {steps.map((step, i) => (
            <AgendaStep key={step.time} step={step} index={i} isLast={i === steps.length - 1} />
          ))}
        </ol>
      </div>
    </section>
  );
};

interface AgendaStepProps {
  step: (typeof steps)[0];
  index: number;
  isLast: boolean;
}

function AgendaStep({ step, index, isLast }: AgendaStepProps) {
  const [ref, visible] = useScrollReveal<HTMLLIElement>(0.3);

  return (
    <li
      ref={ref}
      className="relative grid grid-cols-[64px_1fr] gap-5 transition-all duration-700 sm:grid-cols-[100px_1fr] sm:gap-8"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-16px)',
        transitionDelay: `${index * 90}ms`,
      }}>
      {/* Time gutter */}
      <div className="text-right">
        <div
          className="text-[24px] leading-none tracking-tight text-[#0a1024] sm:text-[30px]"
          style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
          {step.time}
        </div>
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
          {step.duration}
        </div>
      </div>

      {/* Content + connector */}
      <div className="relative pb-12 last:pb-0">
        {/* Connector line */}
        {!isLast && (
          <span
            className="absolute -left-5 top-3 h-full w-px sm:-left-8"
            style={{ background: `linear-gradient(to bottom, ${step.color}40, ${step.color}10)` }}
          />
        )}
        {/* Bullet */}
        <span
          className="absolute -left-[26px] top-2 flex h-3 w-3 items-center justify-center rounded-full sm:-left-[37px]"
          style={{
            background: step.color,
            boxShadow: `0 0 0 4px ${step.color}1a`,
          }}
        />

        <h3
          className="text-[22px] leading-snug tracking-tight text-[#0a1024] sm:text-[26px]"
          style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
          {step.title}
        </h3>

        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-6">
          <div>
            <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              <Icon icon="solar:user-rounded-bold" className="text-[12px]" />
              You
            </dt>
            <dd className="mt-1.5 text-[14px] leading-relaxed text-slate-600">{step.youDo}</dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              <Icon icon="solar:stars-bold" className="text-[12px]" style={{ color: step.color }} />
              Chat.co
            </dt>
            <dd className="mt-1.5 text-[14px] leading-relaxed text-slate-600">{step.weDo}</dd>
          </div>
        </dl>
      </div>
    </li>
  );
}

export default DemoAgenda;
